import { useState, useEffect, useCallback } from 'react';
import { directSupabase } from '../lib/supabaseClient';
import {
  emitProjectCreatedActivity,
  withProjectCreator,
} from '../services/activityEvents.service';
import type { WorkflowTask } from '../components/ProjectWorkflow';
import type { ProjectFormData } from '../types/project';
import { Project } from '../types/project';

// Custom hook for projects list
export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await directSupabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        setError('Failed to fetch projects');
      } else {
        setProjects(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchProject = async (projectId: string) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await directSupabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();
      
      if (error) {
        setError('Failed to fetch project');
        return null;
      } else {
        return data;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (projectId: string, updates: Partial<Project>) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await directSupabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .select()
        .single();
      
      if (error) {
        setError('Failed to update project');
        return null;
      } else {
        // Update the project in the local state
        setProjects(prevProjects =>
          prevProjects.map(project =>
            project.id === projectId ? { ...project, ...data } : project
          )
        );
        return data;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setLoading(true);
      setError(null);
      const insertPayload = await withProjectCreator(projectData as unknown as Record<string, unknown>);
      const { data, error } = await directSupabase
        .from('projects')
        .insert([insertPayload])
        .select()
        .single();
      
      if (error) {
        setError('Failed to create project');
        return null;
      } else {
        emitProjectCreatedActivity({
          projectId: data.id,
          customerId: data.customer_id,
          customerName: data.customer_name,
          actorId: (insertPayload as { created_by?: string }).created_by || null,
        });
        // Add the new project to the local state
        setProjects(prevProjects => [data, ...prevProjects]);
        return data;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId: string) => {
    try {
      setLoading(true);
      setError(null);
      const { error } = await directSupabase.rpc('purge_workspace', { p_project_id: projectId });
      
      if (error) {
        setError(error.message || 'Failed to delete project');
        return false;
      } else {
        // Remove the project from the local state
        setProjects(prevProjects => prevProjects.filter(project => project.id !== projectId));
        return true;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    fetchProject,
    updateProject,
    createProject,
    deleteProject,
  };
};

// Individual project hook
export const useProject = (projectId: string | undefined) => {
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
  
      const { data, error } = await directSupabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();
      
      if (error) {
        setError('Failed to fetch project');
      } else {
        setProject(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async (projectData: Partial<Project>) => {
    if (!projectId) {
      setError('Project ID is required');
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error } = await directSupabase
        .from('projects')
        .update(projectData)
        .eq('id', projectId)
        .select()
        .single();
      
      if (error) {
        setError('Failed to update project');
        return null;
      } else {
        setProject(data);
        return data;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  return {
    project,
    loading,
    error,
    updateProject,
    refetch: fetchProject,
  };
};

// Standalone functions for backward compatibility
export const createProject = async (projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => {
  try {
    const insertPayload = await withProjectCreator(projectData as unknown as Record<string, unknown>);
    const { data, error } = await directSupabase
      .from('projects')
      .insert([insertPayload])
      .select()
      .single();
    
    if (error) {
      throw new Error('Failed to create project');
    }
    emitProjectCreatedActivity({
      projectId: data.id,
      customerId: data.customer_id,
      customerName: data.customer_name,
      actorId: (insertPayload as { created_by?: string }).created_by || null,
    });
    return { success: true, data };
  } catch (err) {
    throw err;
  }
};

export const deleteProject = async (projectId: string) => {
  try {
    const { error } = await directSupabase.rpc('purge_workspace', { p_project_id: projectId });
    
    if (error) {
      throw new Error(error.message || 'Failed to delete project');
    }
    return { success: true };
  } catch (err) {
    throw err;
  }
};

// Helper to normalize a Project to ProjectFormData
function normalizeProjectToFormData(project: Project): ProjectFormData {
  return {
    id: project.id,
    project_name: project.project_name || '',
    customer_id: project.customer_id.toString(), // Convert number to string
    customer_name: project.customer_name || undefined,
    customer_email: project.customer_email || undefined,
    notes: project.notes || '',
    deposit_received: project.deposit_received || false,
    ordered: project.ordered || false,
    delivery_date: project.delivery_date || null,
    install_date: project.install_date || null,
    product_types: Array.isArray(project.product_types) ? project.product_types : [],
    supplier_quote_received: project.supplier_quote_received || false,
    supplier_prices: project.supplier_prices || [],
    complete: project.complete ?? false,
    project_status: project.project_status || 'active',  // Map the new project_status field
    workflow_tasks: project.workflow_tasks || [],
    preferred_suppliers: project.preferred_suppliers || [],
    onedrive_folder_id: project.onedrive_folder_id || null,
    supply_only: project.supply_only || false,
  };
} 